/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { closeDialog } from '../../../app/dialog/dialog.actions';
import { dialogSelectors } from '../../../app/dialog/dialog.selectors';
import {
  checkUserExistence,
  createUser,
  clearUserExistenceCheck,
} from '../../../app/page/page-storage/users/users.actions';
import { useSelect, useSelectWithParams } from '../../../utils/selector.utils';
import { usersPageSelectors } from '../../../app/page/page-storage/users/users.selectors';
import { SubmitButton } from '../../pages/common/forms/SubmitButton';
import { ConnectedDialog } from '../ConnectedDialog';
import { INewUserFormDialogProps } from './types';
import { NewUserForm } from '../../pages/UserPage/NewUserForm';
import { IUser, IUserCreateForm } from '../../../app/resource/user/user.types';
import { addUserToTenant as addUserToTenantAction } from '../../../app/resource/user-tenants/user-tenants.actions';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';

export enum USER_STATE {
  USER_EXISTS_IN_THIS_TENANT,
  USER_EXISTS_IN_ANOTHER_TENANT,
  NEW_USER,
}

export enum STEP {
  ONE,
  TWO,
}

export const NewUserFormDialog: FunctionComponent = () => {
  const dispatch = useDispatch();
  const props = useSelectWithParams(dialogSelectors.selectDialogProps, 'newUserFormDialog') as INewUserFormDialogProps;

  const isDialogOpen = useSelectWithParams(dialogSelectors.selectIsDialogOpen, 'newUserFormDialog');
  const userExistenceCheck = useSelect(usersPageSelectors.selectUserExistenceCheck);

  const [userExistsInTenantError, setUserExistsInTenantError] = useState<boolean>(false);
  const [user, setUser] = useState<Pick<IUser, 'id' | 'firstName' | 'lastName'> | undefined>(undefined);
  const [userState, setUserState] = useState<USER_STATE | null>(null);
  const [step, setStep] = useState<STEP>(STEP.ONE);

  const resetAllState = useCallback(() => {
    setUserState(null);
    setStep(STEP.ONE);
    setUser(undefined);
    setUserExistsInTenantError(false);
  }, []);

  useEffect(() => {
    if (isDialogOpen) {
      resetAllState();
    }
  }, [isDialogOpen, resetAllState]);

  const onClose = useCallback(() => {
    dispatch(closeDialog('newUserFormDialog'));
  }, [dispatch]);

  // React to user existence check result
  useEffect(() => {
    if (userExistenceCheck) {
      if (userExistenceCheck.existsInTenant) {
        setUserState(USER_STATE.USER_EXISTS_IN_THIS_TENANT);
        setUserExistsInTenantError(true);
      } else if (userExistenceCheck.exists) {
        setUserState(USER_STATE.USER_EXISTS_IN_ANOTHER_TENANT);
        setUserExistsInTenantError(false);
        setUser(userExistenceCheck.user);
        setStep(STEP.TWO);
      } else {
        setUserState(USER_STATE.NEW_USER);
        setUserExistsInTenantError(false);
        setStep(STEP.TWO);
      }
      // Clear after processing
      dispatch(clearUserExistenceCheck());
    }
  }, [userExistenceCheck, dispatch]);

  const onSubmit = (data: IUserCreateForm) => {
    if (step === STEP.ONE) {
      verifyIfUserExists(data);
    } else {
      createNewUser(data);
    }
  };
  const verifyIfUserExists = (formData: { email: string }) => {
    dispatch(checkUserExistence(formData.email));
  };

  const createNewUser = (formData: IUserCreateForm) => {
    dispatch(createUser(formData));
  };

  const addUserToTenantHandler = () => {
    if (user) {
      dispatch(addUserToTenantAction(user.id));
    }
  };

  const showBackButton = step === STEP.TWO;

  return (
    <ConnectedDialog dialogKey="newUserFormDialog">
      <div className="popup new_team_popup mid-popup isVisible">
        <div className="panel">
          <div className="panel-header small-header flex jcsb">
            <h3>Add user</h3>
            <span className="close-popup close"></span>
          </div>

          <div className="panel-body pt-0" style={{ maxHeight: '500px' }}>
            {userExistsInTenantError && (
              <div className="red-bg p-1 white radius-5 my-2 max-250" style={{ marginLeft: 15 }}>
                User already exists in this workspace
              </div>
            )}
            {userState === USER_STATE.USER_EXISTS_IN_ANOTHER_TENANT && user && (
              <div className="m-2">
                Are you sure you want to invite{' '}
                <b>
                  {user.firstName} {user.lastName}
                </b>{' '}
                to this workspace?
              </div>
            )}
            {userState === USER_STATE.NEW_USER && (
              <div className="my-2" style={{ marginLeft: 15 }}>
                The user does not exist in the system. Please enter the user&apos;s first name and last name
              </div>
            )}

            {/* we always display the form except the verification said the user already exists in the system */}
            {/* so in that case, we only ask for a confirmation */}
            {userState !== USER_STATE.USER_EXISTS_IN_ANOTHER_TENANT && (
              <NewUserForm {...props} user={user} userState={userState} step={step} onSubmit={onSubmit} />
            )}
          </div>
          <div className={`panel-footer no-border flex ${showBackButton ? 'justify-between' : 'jcr'}`}>
            {showBackButton && (
              <div>
                <a className="link blueGray mr-2 close align-center flex" title="" onClick={resetAllState}>
                  <FaChevronLeft />
                  Go back
                </a>
              </div>
            )}

            <div className="flex align-center jcr">
              <a className="link blueGray mr-2 close" title="" onClick={onClose}>
                Close
              </a>
              {(userState === null || userState === USER_STATE.USER_EXISTS_IN_THIS_TENANT) && (
                <SubmitButton
                  apiKey="createUser"
                  formName="new-user-form"
                  text={
                    <>
                      Next <FaChevronRight className="f-1" />
                    </>
                  }
                />
              )}
              {/* if user is new, display the form and the submit button  */}
              {userState === USER_STATE.NEW_USER && (
                <SubmitButton apiKey="createUser" formName="new-user-form" text="Finish" />
              )}

              {/* if user already exists in another tenant, we don't display the form but we do display the next button */}
              {userState === USER_STATE.USER_EXISTS_IN_ANOTHER_TENANT && (
                <button className="btn btn-green" onClick={addUserToTenantHandler}>
                  Finish
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </ConnectedDialog>
  );
};
